/**
 * Private affiliate invites: sellhiz.win/j/<code>, one person, one use, seven
 * days. Pure: the server, the pages and the unit tests share it.
 */
import { z } from "zod";
import { commissionLabel, formatRm, type JoinCommission } from "./affiliateJoin";

export const INVITE_TTL_DAYS = 7;
export const INVITE_LABEL_MAX = 80;
/** No 0/O or 1/I: the code is read off a phone screen and sometimes typed. */
const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const CODE = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/;

export function newInviteCode(random: Uint8Array): string {
  if (random.length < 8) throw new Error("newInviteCode needs 8 random bytes");
  let s = "";
  for (let i = 0; i < 8; i++) s += ALPHABET[random[i] % ALPHABET.length];
  return `${s.slice(0, 4)}-${s.slice(4)}`;
}

export function isInviteCode(code: string): boolean {
  return CODE.test(code);
}

export function affiliateInvitePath(code: string): string {
  return `/j/${code}`;
}

/** The code in /j/<code>, upper-cased, or null. */
export function parseInvitePath(pathname: string): string | null {
  const m = /^\/j\/([A-Za-z0-9]{4}-[A-Za-z0-9]{4})\/?$/.exec(pathname);
  return m ? m[1].toUpperCase() : null;
}

export type InviteState = "fresh" | "opened" | "joined" | "sold" | "expired" | "revoked";

/** Where an invite stands, for the seller's list and for the page it opens. */
export function inviteState(
  row: { opened_at: string | null; accepted_at: string | null; revoked_at: string | null; expires_at: string },
  now: number,
  hasSale = false,
): InviteState {
  if (row.accepted_at) return hasSale ? "sold" : "joined";
  if (row.revoked_at) return "revoked";
  if (Date.parse(row.expires_at) <= now) return "expired";
  return row.opened_at ? "opened" : "fresh";
}

/** Still usable by the person it was made for. */
export function inviteUsable(state: InviteState): boolean {
  return state === "fresh" || state === "opened";
}

/**
 * Who the invite is for, as the seller types it: a name, a nickname, a shop
 * name, or a phone number. Letters of any language, digits, spaces and the
 * punctuation names carry; nothing that could be markup.
 */
export const inviteLabelSchema = z
  .string({ required_error: "Tulis nama orang yang dijemput" })
  .transform((s) => s.replace(/\s+/g, " ").trim())
  .pipe(
    z
      .string()
      .min(1, "Tulis nama orang yang dijemput")
      .max(INVITE_LABEL_MAX, `Nama terlalu panjang (maksimum ${INVITE_LABEL_MAX} aksara)`)
      .regex(/^[\p{L}\p{M}\p{N} .,'’\-@+()&/]+$/u, "Nama hanya boleh ada huruf, nombor dan tanda biasa"),
  );

export const inviteCreateSchema = z.object({ label: inviteLabelSchema });
export type InviteCreateInput = z.infer<typeof inviteCreateSchema>;

/** The first word of the label, for "Salam Hafiz". A phone number gets no name. */
export function inviteGreetingName(label: string): string | null {
  const first = label.trim().split(/\s+/)[0] ?? "";
  return /\p{L}/u.test(first) ? first : null;
}

/** The personal message the seller sends with the link. */
export function privateInviteMessage(input: {
  label: string;
  storeName: string;
  commission: JoinCommission;
  example?: { name: string; earnCents: number } | null;
  url: string;
}): string {
  const name = inviteGreetingName(input.label);
  const example =
    input.example && input.example.earnCents > 0 ? `, contohnya ${formatRm(input.example.earnCents)} untuk ${input.example.name}` : "";
  return [
    `${name ? `Salam ${name}` : "Salam"}, saya nak jemput awak jadi affiliate ${input.storeName.trim()}.`,
    `Awak dapat ${commissionLabel(input.commission)} setiap jualan${example}.`,
    "Link ni khas untuk awak, terus aktif bila daftar:",
    input.url,
  ].join("\n");
}
